import React, { useEffect, useState } from 'react';
import {
    Card,
    Row,
    Col,
    Form,
    Input,
    Select,
    Button,
    Typography,
    Space,
    Spin,
    Empty,
    Tag,
    Avatar,
    Divider,
    Descriptions,
} from 'antd';
import {
    UserOutlined,
    SaveOutlined,
    EditOutlined,
    MailOutlined,
    IdcardOutlined,
    DollarOutlined,
    CrownOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import { useDetalleUsuario, useRolesSelect, useTipoUsuarioSelect } from '../../../../Hooks/Admin';
import { useDataUsuarioSelected } from '../../../../Hooks/Admin/useContextos';
import { useEditarUsuario } from '../queries/querysUsuario';

const { Title, Text } = Typography;

const inputStyle = {
    borderRadius: 6,
};

export const DataUsuario = () => {
    const { selectedId } = useDataUsuarioSelected();
    const [form] = Form.useForm();
    const [detalleDatos, setDetalleDatos] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const queryDetalle = useDetalleUsuario({ idUsuario: selectedId });
    const tipoUsuario = useTipoUsuarioSelect();
    const roles = useRolesSelect();
    const queryEditarUsuario = useEditarUsuario();

    useEffect(() => {
        if (selectedId) {
            fetchData();
        }
    }, [selectedId]);

    useEffect(() => {
        if (detalleDatos) {
            form.setFieldsValue(detalleDatos);
        }
    }, [detalleDatos, form]);

    const fetchData = async () => {
        try {
            const respuesta = await queryDetalle.refetch({ idUsuario: selectedId });
            setDetalleDatos(respuesta.data);
        } catch (error) {
            console.error('Error al cargar datos del usuario:', error);
        }
    };

    const handleSubmit = async (values) => {
        try {
            setIsSubmitting(true);
            const dataToSend = {
                ...values,
                idUsuario: selectedId,
                wonderCoins: parseInt(values.wonderCoins) || 0,
            };

            await queryEditarUsuario.mutateAsync(dataToSend);
            await fetchData();
            setEditMode(false);
        } catch (error) {
            console.error('Error al editar usuario:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setEditMode(false);
        if (detalleDatos) {
            form.setFieldsValue(detalleDatos);
        }
    };

    const tipoUsuarioOptions = tipoUsuario.data || [];
    const rolesOptions = roles.data || [];

    if (!selectedId) {
        return (
            <Card>
                <Empty
                    description="Selecciona un usuario de la lista para ver y editar su información"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            </Card>
        );
    }

    if (queryDetalle.isLoading || !detalleDatos) {
        return (
            <Card>
                <div style={{ textAlign: 'center', padding: '48px 0' }}>
                    <Spin size="large" />
                    <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
                        Cargando datos del usuario...
                    </Text>
                </div>
            </Card>
        );
    }

    return (
        <>
            {/* Header */}
            <Card style={{ marginBottom: 16, borderRadius: 8 }}>
                <Row align="middle" justify="space-between">
                    <Col>
                        <Space size="middle" align="center">
                            <Avatar
                                size={64}
                                src={detalleDatos.imagen}
                                icon={<UserOutlined />}
                            />
                            <div>
                                <Title level={4} style={{ margin: 0 }}>
                                    {detalleDatos.nombre}
                                </Title>
                                <Text type="secondary">
                                    Entrenador #{detalleDatos.identificador} • {detalleDatos.wonderCoins} Wonder Coins
                                </Text>
                            </div>
                        </Space>
                    </Col>
                    <Col>
                        {editMode && (
                            <Tag color="blue" style={{ marginRight: 12 }}>Modo edición</Tag>
                        )}
                        {!editMode ? (
                            <Button
                                type="primary"
                                icon={<EditOutlined />}
                                onClick={() => setEditMode(true)}
                            >
                                Editar
                            </Button>
                        ) : (
                            <Button onClick={handleCancel}>
                                Cancelar
                            </Button>
                        )}
                    </Col>
                </Row>
            </Card>

            {/* Formulario */}
            <Card
                title={
                    <Space>
                        <UserOutlined />
                        <span>Información del Usuario</span>
                    </Space>
                }
                style={{ borderRadius: 8 }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    disabled={!editMode}
                    size="large"
                >
                    {/* Datos Personales */}
                    <Divider orientation="left" plain>
                        <Text strong>Datos Personales</Text>
                    </Divider>

                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label="Nombre Usuario"
                                name="nombre"
                                rules={[{ required: true, message: 'El nombre es obligatorio' }]}
                            >
                                <Input
                                    placeholder="Nombre de usuario"
                                    style={inputStyle}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label="Nombre Real"
                                name="nombreReal"
                            >
                                <Input
                                    placeholder="Nombre real del usuario"
                                    style={inputStyle}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label="Correo"
                                name="correo"
                                rules={[
                                    { required: true, message: 'El correo es obligatorio' },
                                    { type: 'email', message: 'Correo inválido' }
                                ]}
                            >
                                <Input
                                    placeholder="correo@ejemplo.com"
                                    style={inputStyle}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label="Identificador"
                                name="identificador"
                                rules={[{ required: true, message: 'El identificador es obligatorio' }]}
                            >
                                <Input
                                    placeholder="ID único"
                                    style={inputStyle}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label="Wonder Coins"
                                name="wonderCoins"
                                rules={[{ required: true, message: 'Las Wonder Coins son obligatorias' }]}
                            >
                                <Input
                                    type="number"
                                    placeholder="0"
                                    style={inputStyle}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Permisos y Roles */}
                    <Divider orientation="left" plain style={{ marginTop: 8 }}>
                        <Text strong>Permisos y Roles</Text>
                    </Divider>

                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Tipo Usuario"
                                name="tipoUsuario"
                                rules={[{ required: true, message: 'Seleccione un tipo de usuario' }]}
                            >
                                <Select
                                    placeholder="Seleccionar tipo"
                                    options={tipoUsuarioOptions}
                                    style={{ borderRadius: 6 }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Rol"
                                name="rol"
                                rules={[{ required: true, message: 'Seleccione un rol' }]}
                            >
                                <Select
                                    placeholder="Seleccionar rol"
                                    options={rolesOptions}
                                    style={{ borderRadius: 6 }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {editMode && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                loading={isSubmitting}
                                icon={<SaveOutlined />}
                            >
                                Guardar Cambios
                            </Button>
                        </div>
                    )}
                </Form>
            </Card>
        </>
    );
};

export default DataUsuario;